import clsx from 'clsx';
import { Link } from 'react-router-dom';
import type { ConversationInfo } from '../../stores/klanten-store';
import { BRAND_COLORS } from '../../lib/order-constants';
import { formatDate } from '../../lib/format';

interface Props {
  conversations: ConversationInfo[];
}

const STATUS_COLORS: Record<string, string> = {
  nieuw: 'bg-blue-100 text-blue-700',
  in_behandeling: 'bg-amber-100 text-amber-700',
  beantwoord: 'bg-emerald-100 text-emerald-700',
  afgehandeld: 'bg-slate-100 text-slate-500',
};

function channelIcon(channel: string) {
  if (channel === 'phone') {
    return (
      <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    );
  }
  if (channel === 'whatsapp') {
    return (
      <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

export function BerichtenTab({ conversations }: Props) {
  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400">
        <p className="text-sm">Geen berichten gevonden</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {conversations.map((c) => (
        <Link
          key={c.id}
          to={`/inbox/${c.id}`}
          className="flex items-start gap-3 px-6 py-3 hover:bg-slate-50 transition-colors"
        >
          <div className="pt-0.5 flex-shrink-0">{channelIcon(c.channel)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-medium text-slate-900 truncate">
                {c.subject || c.fromName || '(Geen onderwerp)'}
              </span>
              {c.brand && (
                <span className={clsx(
                  'text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0',
                  BRAND_COLORS[c.brand] || 'bg-slate-100 text-slate-600'
                )}>
                  {c.brand === 'SCHOONHUISSHOP' ? 'SHS' : c.brand}
                </span>
              )}
              <span className={clsx(
                'text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0',
                STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-500'
              )}>
                {c.status}
              </span>
            </div>
            <p className="text-xs text-slate-500 truncate">{c.snippet}</p>
          </div>
          <span className="text-xs text-slate-400 flex-shrink-0 pt-0.5">
            {c.lastMessageAt && formatDate(c.lastMessageAt)}
          </span>
        </Link>
      ))}
    </div>
  );
}
